import zenthom from "../assets/images/zenthom-project.jpg";
import hutstack from "../assets/images/hutstack-project.jpg";
import aether from "../assets/images/aether-route-project.jpg";
import { Project } from "../types/project";
export const featured: Project[] = [
  {
    slug: "zenthom",
    name: "Zenthom",
    category: "Facility maintenance SaaS",
    role: "Software Engineer",
    summary:
      "One product. Three portals. A clearer way to manage property maintenance.",
    image: zenthom,
    theme: "sage",
    tags: ["React", "TypeScript", "API integration"],
    live: "https://zenthom.com/",
    context:
      "Zenthom brings facility maintenance into role-specific workspaces for HQ administrators, facility managers, and customers. Each group needs a different view of the same operation.",
    responsibilities: [
      "Independently developed the frontend across the HQ, facility manager, and customer portals.",
      "Built dashboards, maintenance workflows, reporting, customer management, and subscription interfaces.",
      "Structured reusable components, custom hooks, and API service layers across the three portals.",
      "Validated supported API workflows, documented inconsistent customer IDs and response formats, and coordinated missing-endpoint fixes with the backend developer.",
    ],
    decisions: [
      {
        title: "Shared foundations, distinct workflows",
        text: "The three portals share frontend foundations while keeping role-specific screens and workflows separate. Reusable components, hooks, and service layers support this structure.",
      },
      {
        title: "Integration follows the actual API",
        text: "The backend collection contained 140+ documented operations. I validated responses and integrated supported workflows, recording missing endpoints and inconsistent responses as blockers.",
      },
    ],
    outcome:
      "Developed the frontend foundation and role-specific interfaces for a three-portal facility maintenance product, integrated supported API workflows, and documented backend blockers to support continued delivery.",
    credit:
      "Professional work · Public marketing screenshot. Private portal visuals are not included.",
  },
  {
    slug: "hutstack",
    name: "Hutstack",
    category: "Property management SaaS",
    role: "Frontend Developer · Insightful3D",
    summary:
      "Bringing properties, tenancies, leases, and financial workflows into focus.",
    image: hutstack,
    theme: "slate",
    tags: ["React", "TypeScript", "Redux Toolkit"],
    live: "https://www.hutstack.com/",
    context:
      "Property managers need a consistent workspace for daily operations, from tenancy records and leases to maintenance tracking and financial analytics. I led frontend development of Hutstack during my time at Insightful3D.",
    responsibilities: [
      "Built interfaces for property, lease, and tenancy management.",
      "Developed maintenance tracking and financial analytics interfaces.",
      "Integrated backend services into frontend workflows and investigated API issues.",
      "Improved interface consistency across the product.",
    ],
    decisions: [
      {
        title: "Consistency across operational screens",
        text: "The work focused on connecting related property workflows through consistent frontend interfaces and reusable UI patterns.",
      },
      {
        title: "API issues are part of interface delivery",
        text: "Backend integration included investigating API issues so the interface could reflect the data and workflows actually supported by the service.",
      },
    ],
    outcome:
      "Contributed the frontend interfaces for Hutstack’s core property management workflows during February to July 2025.",
    credit:
      "Professional work at Insightful3D · Current public website shown for product context.",
  },
  {
    slug: "driwego",
    name: "Driwego",
    category: "B2C & B2B commerce",
    role: "Next.js Frontend Developer · Contract",
    summary:
      "Storefront experiences and vendor tools, built around real commerce workflows.",
    theme: "sand",
    tags: ["Next.js", "Medusa.js", "TanStack Query"],
    context:
      "Driwego was professional contract work through Cartier Creative, covering B2C and B2B storefront interfaces and vendor panel features. It is not an independently owned product.",
    responsibilities: [
      "Built responsive storefront interfaces using Next.js, TypeScript, Tailwind CSS, and shadcn/ui.",
      "Developed vendor panel features using Medusa.js APIs and SDK.",
      "Used TanStack Query, React Hook Form, and Zod for data and form workflows.",
      "Translated Figma designs into reusable components and refined features through pull requests, reviews, and responsive testing.",
    ],
    decisions: [
      {
        title: "Purpose-built data and form tools",
        text: "TanStack Query supported server-data workflows, while React Hook Form and Zod supported the vendor panel’s form handling and validation.",
      },
      {
        title: "Design to reviewed implementation",
        text: "The delivery process connected Figma designs, reusable components, pull requests, review feedback, and responsive testing.",
      },
    ],
    outcome:
      "Delivered storefront interfaces and vendor panel features during the July to October 2025 contract with Cartier Creative.",
    credit: "Contract frontend development through Cartier Creative.",
  },
  {
    slug: "aether-route",
    name: "Aether Route",
    category: "Route comparison application",
    role: "Personal project · Full-stack development",
    summary:
      "A better view of the journey, with mapping, weather, and route risk analysis.",
    image: aether,
    theme: "forest",
    tags: ["React", "TypeScript", "Django"],
    live: "https://aether-route.vercel.app/",
    github: "https://github.com/Fabulousjay/aether-route",
    context:
      "Aether Route compares routes using weather forecasts and risk analysis. It explores how a journey can be understood through more than distance alone.",
    responsibilities: [
      "Built a route comparison application with React, TypeScript, and Django.",
      "Brought mapping, weather forecasts, and route risk analysis into the application.",
    ],
    decisions: [
      {
        title: "Route context in one application",
        text: "The product combines route comparison with weather and risk information so users can consider conditions alongside the journey.",
      },
      {
        title: "Frontend and backend practice",
        text: "The project combines a typed React frontend with a Django backend, extending my work across the interface and API boundary.",
      },
    ],
    outcome:
      "A live route comparison application bringing mapping, weather forecasts, and route risk analysis together, with publicly available source code.",
    credit:
      "Personal project · Screenshot captured from the public application.",
  },
];
